import { useEffect, useMemo, useRef, useState } from 'react';
import { useMachineSetupStore } from '../../stores/machine-setup-store';
import type { WizardData, MachineGroupInfo, MachineType, AttributeMismatch } from '../../types/machine-setup-types';

interface ConfigStepProps {
  wizardData: Partial<WizardData>;
  onChange: (data: Partial<WizardData>) => void;
  machineTypes?: MachineType[];
  /** Update mode: show inline "Currently on machine" hints + section Apply Defaults buttons */
  mismatches?: AttributeMismatch[];
  /** Update mode: hide the machine group selector (not changed via applyAttributes) */
  hideGroupSelector?: boolean;
}

// ── Static option lists (from Lynx attribute catalog) ────────────────────────

const MAX_CURRENT_OPTIONS = ['6', '8', '10', '13', '16', '20', '25', '32'];

const LANGUAGE_OPTIONS = [
  { code: '1', label: 'Hebrew' },
  { code: '0', label: 'English' },
  { code: '2', label: 'French' },
  { code: '5', label: 'German' },
  { code: '6', label: 'Russian' },
];

const CONNECTOR_FORMAT_OPTIONS = [
  { code: '2', label: 'Socket' },
  { code: '1', label: 'Cable' },
];

const SOCKET_TYPE_OPTIONS = [
  { code: '10', label: 'Type 2' },
  { code: '5',  label: 'Type 1' },
  { code: '40', label: 'CCS2' },
  { code: '35', label: 'CCS1' },
  { code: '45', label: 'CHAdeMO' },
  { code: '32', label: 'NACS' },
  { code: '25', label: 'GB/T' },
];

const PHASES_OPTIONS = [
  { code: '3', label: '3-phase' },
  { code: '1', label: '1-phase' },
];

const CHARGING_TYPE_OPTIONS = [
  { code: '1', label: 'AC' },
  { code: '2', label: 'DC' },
];

// ── Attribute ID ↔ wizard field mapping (exported for update wizard) ─────────

export const FIELD_ATTR_ID: Partial<Record<keyof WizardData, number>> = {
  defaultLanguage:   7105,
  tariffId:          7002,
  defaultCredit:     584,
  maxCurrentEvse1:   5716,
  maxCurrentEvse2:   5726,
  chargerMaxCurrent: 7015,
  connectorFormat1:  5710,
  socketType1:       5711,
  noOfPhases1:       5713,
  chargingType1:     5714,
  maxPowerKw1:       5715,
  minCurrentEvse1:   5717,
  connectorFormat2:  5720,
  socketType2:       5721,
  noOfPhases2:       5723,
  chargingType2:     5724,
  maxPowerKw2:       5725,
  minCurrentEvse2:   5727,
};

/** Fields where wizardData stores the codeId as a string (e.g. '10' for Type 2) */
export const CODE_BASED_WIZARD_FIELDS = new Set<keyof WizardData>([
  'defaultLanguage',
  'connectorFormat1', 'socketType1', 'noOfPhases1', 'chargingType1',
  'connectorFormat2', 'socketType2', 'noOfPhases2', 'chargingType2',
]);

// ── Section field groups (for Apply Defaults per section) ────────────────────

const GENERAL_FIELDS:      (keyof WizardData)[] = ['defaultLanguage'];
const PAYMENT_FIELDS:      (keyof WizardData)[] = ['tariffId', 'defaultCredit'];
const VPOST_CHARGER_FIELDS:(keyof WizardData)[] = ['maxCurrentEvse1', 'maxCurrentEvse2'];
const EV_CHARGER_FIELDS:   (keyof WizardData)[] = ['chargerMaxCurrent'];
const CONNECTOR1_FIELDS:   (keyof WizardData)[] = [
  'connectorFormat1', 'socketType1', 'noOfPhases1', 'chargingType1',
  'maxPowerKw1', 'maxCurrentEvse1', 'minCurrentEvse1',
];
const CONNECTOR2_FIELDS:   (keyof WizardData)[] = [
  'connectorFormat2', 'socketType2', 'noOfPhases2', 'chargingType2',
  'maxPowerKw2', 'maxCurrentEvse2', 'minCurrentEvse2',
];

// ── Machine type group helpers ────────────────────────────────────────────────

function inGroup(groups: string[] | undefined, group: string): boolean {
  return groups?.includes(group) ?? false;
}

// ── Tariff ID builder helpers ────────────────────────────────────────────────

/** Parse a tariff string like "S200wp95v18" into its three numeric parts. */
function parseTariffId(val: string): { s: string; p: string; v: string } {
  const sMatch = val.match(/^S(\d+)/i);
  const pMatch = val.match(/p(\d+)/i);
  const vMatch = val.match(/v(\d+)/i);
  return {
    s: sMatch ? sMatch[1] : '',
    p: pMatch ? pMatch[1] : '',
    v: vMatch ? vMatch[1] : '',
  };
}

/** Build a tariff string from its three parts. S is omitted when empty or "0". */
function buildTariffId(s: string, p: string, v: string): string {
  const sPart = s && s !== '0' ? `S${s}` : '';
  const pPart = p ? `p${p}` : '';
  const vPart = v && v !== '0' ? `v${v}` : '';
  return `${sPart}w${pPart}${vPart}`;
}

// ── Per-type defaults ────────────────────────────────────────────────────────

/** Build a lookup from attribute ID string → wizard field name (inverse of FIELD_ATTR_ID). */
const ATTR_ID_TO_FIELD: Record<string, keyof WizardData> = Object.fromEntries(
  Object.entries(FIELD_ATTR_ID).map(([field, id]) => [String(id), field as keyof WizardData])
);

/**
 * Returns static-attribute overrides as wizard field values.
 * Only covers fields listed in FIELD_ATTR_ID; unknown attrs are ignored.
 */
function staticAttrOverrides(staticDefaults: Record<string, string> | undefined): Partial<WizardData> {
  if (!staticDefaults) return {};
  const overrides: Partial<WizardData> = {};
  for (const [attrId, value] of Object.entries(staticDefaults)) {
    const field = ATTR_ID_TO_FIELD[attrId];
    if (field) (overrides as Record<string, string>)[field] = value;
  }
  return overrides;
}

export function getDefaults(key: string, groups?: string[], staticDefaults?: Record<string, string>): Partial<WizardData> {
  const isPublicEv = inGroup(groups, 'public_ev');
  const isDuoEv    = inGroup(groups, 'duo_ev');
  const attrOvr    = staticAttrOverrides(staticDefaults);

  const evBase: Partial<WizardData> = {
    defaultLanguage:   '',
    chargerMaxCurrent: '32',
    socketType1:       '10',  // Type2
    noOfPhases1:       '3',   // 3-phase
    chargingType1:     '1',   // AC
    maxPowerKw1:       '22',
    maxCurrentEvse1:   '32',
    minCurrentEvse1:   '6',
  };

  if (isPublicEv) {
    const duoExtra: Partial<WizardData> = isDuoEv ? {
      chargerMaxCurrent: '32,32',
      connectorFormat2:  '2',
      socketType2:       '10',
      noOfPhases2:       '3',
      chargingType2:     '1',
      maxPowerKw2:       '22',
      maxCurrentEvse2:   '32',
      minCurrentEvse2:   '6',
    } : {};
    return {
      ...evBase,
      defaultCredit:    '15000',
      tariffId:         'wp150v18',
      connectorFormat1: '2',
      ...duoExtra,
      ...attrOvr,
    };
  }

  if (key === 'medias4_multi_residential') {
    return {
      ...evBase,
      defaultCredit:     '',
      tariffId:          '',
      chargerMaxCurrent: '16',
      maxCurrentEvse1:   '16',
      ...attrOvr,
    };
  }

  // vpos_*, kiosk and custom types
  return { defaultCredit: '0', ...attrOvr };
}

export function ConfigStep({ wizardData, onChange, machineTypes, mismatches, hideGroupSelector }: ConfigStepProps) {
  const key = wizardData.machineTypeKey ?? '';
  const selectedMachineType = machineTypes?.find(t => t.key === key);
  const typeGroups = selectedMachineType?.groups;
  const typeStaticDefaults = selectedMachineType?.staticAttributeDefaults;
  const isEvCharger  = inGroup(typeGroups, 'ev_charger');
  const isPublicEv   = inGroup(typeGroups, 'public_ev');
  const isDuoEv      = inGroup(typeGroups, 'duo_ev');
  const hasEvse      = inGroup(typeGroups, 'has_evse_current');
  const hasDuoEvse   = inGroup(typeGroups, 'has_duo_evse_current');

  const { loadMachineGroups } = useMachineSetupStore();

  const [machineGroups, setMachineGroups]   = useState<MachineGroupInfo[]>([]);
  const [groupsLoading, setGroupsLoading]   = useState(false);
  const [groupsError,   setGroupsError]     = useState<string | null>(null);
  const [groupsRetryKey, setGroupsRetryKey] = useState(0);

  // Load machine groups for the selected actor
  useEffect(() => {
    if (hideGroupSelector) return;
    const actorId = wizardData.operatorActorId || wizardData.actorId;
    if (!actorId || actorId <= 0) return;

    setMachineGroups([]);
    setGroupsLoading(true);
    setGroupsError(null);
    // Always clear the selection — the previous group belongs to the previous operator
    // and may not exist under the newly selected one.
    onChange({ machineGroupId: undefined, machineGroupName: '' });

    loadMachineGroups(actorId).then((resp) => {
      if (!resp.isSuccess) {
        setGroupsError(resp.message ?? 'Could not load machine groups');
        return;
      }
      if (resp.machineGroups.length === 0) {
        setGroupsError('This operator has no active machine groups. Please create one in Lynx before continuing.');
        return;
      }
      setMachineGroups(resp.machineGroups);
      // Auto-select when there is exactly one group (always safe since we cleared above)
      if (resp.machineGroups.length === 1) {
        onChange({ machineGroupId: resp.machineGroups[0].machineGroupId, machineGroupName: resp.machineGroups[0].machineGroupName });
      }
    }).catch((err: unknown) => {
      setGroupsError(err instanceof Error ? err.message : 'Failed to load machine groups');
    }).finally(() => {
      setGroupsLoading(false);
    });
  }, [wizardData.operatorActorId, wizardData.actorId, hideGroupSelector, loadMachineGroups, groupsRetryKey]);

  // Reset to defaults whenever machine type changes OR when groups load for the first time for the current key
  const prevAppliedRef = useRef<{ key: string; groups: string[] | undefined }>({ key: '', groups: undefined });
  useEffect(() => {
    if (!key) return;
    const { key: prevKey, groups: prevGroups } = prevAppliedRef.current;
    const keyChanged   = key !== prevKey;
    const groupsReady  = typeGroups !== undefined && prevGroups === undefined && key === prevKey;
    if (keyChanged || groupsReady) {
      prevAppliedRef.current = { key, groups: typeGroups };
      onChange(getDefaults(key, typeGroups, typeStaticDefaults));
    }
  }, [key, typeGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mismatch helpers ─────────────────────────────────────────────────────────

  const mismatchMap = useMemo(() => {
    if (!mismatches?.length) return {} as Record<number, AttributeMismatch>;
    const m: Record<number, AttributeMismatch> = {};
    mismatches.forEach(mm => { m[mm.deviceAttributeId] = mm; });
    return m;
  }, [mismatches]);

  const getFieldMismatch = (field: keyof WizardData): AttributeMismatch | undefined => {
    const attrId = FIELD_ATTR_ID[field];
    return attrId !== undefined ? mismatchMap[attrId] : undefined;
  };

  const sectionHasMismatches = (fields: (keyof WizardData)[]): boolean =>
    fields.some(f => getFieldMismatch(f) !== undefined);

  const applyDefaults = (fields: (keyof WizardData)[]) => {
    const allDefaults = getDefaults(key, typeGroups, typeStaticDefaults) as Record<string, unknown>;
    const partial: Partial<WizardData> = {};
    fields.forEach(f => {
      if (f in allDefaults) (partial as Record<string, unknown>)[f] = allDefaults[f];
    });
    onChange(partial);
  };

  const mismatchHint = (field: keyof WizardData) => {
    const m = getFieldMismatch(field);
    if (!m) return null;
    return (
      <p className="mt-1 text-xs text-amber-600">
        Currently on machine: <span className="font-mono">{m.actualValue || '(empty)'}</span>
      </p>
    );
  };

  // ── Styles ───────────────────────────────────────────────────────────────────

  const inputClass  = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring';
  const selectClass = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring bg-white';
  const hint = (text: string) => <p className="mt-1 text-xs text-gray-400">{text}</p>;

  const showTariff        = isPublicEv;
  const showDefaultCredit = isPublicEv || !isEvCharger;

  const sectionHeader = (title: string, sectionFields?: (keyof WizardData)[]) => {
    const hasMismatches = mismatches?.length && sectionFields && sectionHasMismatches(sectionFields);
    return (
      <div className="pt-2 pb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h3>
          {hasMismatches && (
            <button
              type="button"
              onClick={() => applyDefaults(sectionFields!)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Apply Defaults
            </button>
          )}
        </div>
        <div className="mt-1 border-t border-gray-100" />
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Configuration</h2>
      <p className="text-sm text-gray-500 mb-6">
        Set the operating parameters for this machine. Defaults are pre-filled based on the machine type.
      </p>
      <div className="space-y-5 max-w-lg">

        {/* ── General ─────────────────────────────────────────────────────── */}
        {sectionHeader('General', GENERAL_FIELDS)}

        {/* Machine Group */}
        {!hideGroupSelector && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Machine Group <span className="text-red-500">*</span>
            </label>
            {groupsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                Loading machine groups…
              </div>
            ) : groupsError ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-red-500">{groupsError}</p>
                <button
                  type="button"
                  className="text-sm text-primary underline hover:text-primary/80"
                  onClick={() => setGroupsRetryKey((k) => k + 1)}
                >
                  Retry
                </button>
              </div>
            ) : (
              <select
                value={wizardData.machineGroupId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const group = machineGroups.find((g) => g.machineGroupId === id);
                  onChange({ machineGroupId: id, machineGroupName: group?.machineGroupName ?? '' });
                }}
                className={selectClass}
              >
                <option value="" disabled>Select a machine group…</option>
                {machineGroups.map((g) => (
                  <option key={g.machineGroupId} value={g.machineGroupId}>{g.machineGroupName}</option>
                ))}
              </select>
            )}
            {hint('Groups organize machines within an operator for filtering and reporting.')}
          </div>
        )}

        {/* Default Language */}
        {isEvCharger && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
            <select
              value={wizardData.defaultLanguage ?? ''}
              onChange={(e) => onChange({ defaultLanguage: e.target.value })}
              className={selectClass}
            >
              <option value="">Select language…</option>
              {LANGUAGE_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
            {hint('Language shown on the charger screen by default. Users can change it at the machine.')}
            {mismatchHint('defaultLanguage')}
          </div>
        )}

        {/* ── Payment ─────────────────────────────────────────────────────── */}
        {(showTariff || showDefaultCredit) && sectionHeader('Payment', PAYMENT_FIELDS)}

        {/* EV Tier Pricing / Tariff */}
        {showTariff && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">EV Tier Pricing (Tariff ID)</label>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">S — Flat connection fee</label>
                <input
                  type="number"
                  min="0"
                  value={parseTariffId(wizardData.tariffId ?? '').s}
                  onChange={(e) => {
                    const { p, v } = parseTariffId(wizardData.tariffId ?? '');
                    onChange({ tariffId: buildTariffId(e.target.value, p, v) });
                  }}
                  placeholder="0 (optional)"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">P — Price per kWh</label>
                <input
                  type="number"
                  min="0"
                  value={parseTariffId(wizardData.tariffId ?? '').p}
                  onChange={(e) => {
                    const { s, v } = parseTariffId(wizardData.tariffId ?? '');
                    onChange({ tariffId: buildTariffId(s, e.target.value, v) });
                  }}
                  placeholder="e.g. 150"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">V — VAT %</label>
                <input
                  type="number"
                  min="0"
                  value={parseTariffId(wizardData.tariffId ?? '').v}
                  onChange={(e) => {
                    const { s, p } = parseTariffId(wizardData.tariffId ?? '');
                    onChange({ tariffId: buildTariffId(s, p, e.target.value) });
                  }}
                  placeholder="0 (optional)"
                  className={inputClass}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              S and P are in smallest currency unit (e.g. 150 = ₪1.50). V is display-only on the receipt.
              Tariff string: <span className="font-mono text-gray-600">{wizardData.tariffId || '—'}</span>
            </p>
            {mismatchHint('tariffId')}
          </div>
        )}

        {/* Default Credit */}
        {showDefaultCredit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Credit</label>
            <input
              type="text"
              value={wizardData.defaultCredit ?? ''}
              onChange={(e) => onChange({ defaultCredit: e.target.value })}
              placeholder="e.g. 15000"
              className={inputClass}
            />
            {hint('Integer. Pre-authorized credit in smallest currency unit. ILS: 15000 = ₪150, 0 = unlimited.')}
            {mismatchHint('defaultCredit')}
          </div>
        )}

        {/* ── Charger Settings (VPOST: EVSE current only) ──────────────────── */}
        {hasEvse && !isEvCharger && sectionHeader('Charger Settings', VPOST_CHARGER_FIELDS)}

        {/* EVSE Max Current (attr 5716) — for vpos types */}
        {hasEvse && !isEvCharger && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">EVSE Max Current [A]</label>
            <select
              value={wizardData.maxCurrentEvse1 ?? '32'}
              onChange={(e) => onChange({ maxCurrentEvse1: e.target.value })}
              className={selectClass}
            >
              {MAX_CURRENT_OPTIONS.map((v) => (
                <option key={v} value={v}>{v} A</option>
              ))}
            </select>
            {hint('Maximum current for EVSE port 1. Must not exceed the charger hardware rating.')}
            {mismatchHint('maxCurrentEvse1')}
          </div>
        )}

        {/* EVSE 2 Max Current (attr 5726) — for vpos_duo */}
        {hasDuoEvse && !isEvCharger && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">EVSE 2 Max Current [A]</label>
            <select
              value={wizardData.maxCurrentEvse2 ?? '32'}
              onChange={(e) => onChange({ maxCurrentEvse2: e.target.value })}
              className={selectClass}
            >
              {MAX_CURRENT_OPTIONS.map((v) => (
                <option key={v} value={v}>{v} A</option>
              ))}
            </select>
            {hint('Maximum current for EVSE port 2. Must not exceed the charger hardware rating.')}
            {mismatchHint('maxCurrentEvse2')}
          </div>
        )}

        {/* ── Charger Settings (medias4: full connector config) ─────────────── */}
        {isEvCharger && sectionHeader('Charger Settings', EV_CHARGER_FIELDS)}

        {/* Charger Max Electrical Current (attr 7015) */}
        {isEvCharger && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Charger Max Current [A]</label>
            <input
              type="text"
              value={wizardData.chargerMaxCurrent ?? (isDuoEv ? '32,32' : '32')}
              onChange={(e) => onChange({ chargerMaxCurrent: e.target.value })}
              placeholder={isDuoEv ? '32,32' : '32'}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-400">
              {isDuoEv ? 'One value per connector, comma-separated (e.g. 32,32). Range: 6–32.' : 'Range: 6–32 A.'}
            </p>
            {mismatchHint('chargerMaxCurrent')}
          </div>
        )}

        {/* ── Connector 1 ─────────────────────────────────────────────────── */}
        {isEvCharger && (
          <div className="rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Connector 1</h3>
              {mismatches?.length && sectionHasMismatches(CONNECTOR1_FIELDS) && (
                <button
                  type="button"
                  onClick={() => applyDefaults(CONNECTOR1_FIELDS)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Apply Defaults
                </button>
              )}
            </div>

            {isPublicEv && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                <select
                  value={wizardData.connectorFormat1 ?? '2'}
                  onChange={(e) => onChange({ connectorFormat1: e.target.value })}
                  className={selectClass}
                >
                  {CONNECTOR_FORMAT_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
                {hint('Cable = tethered cable fixed to charger. Socket = open socket, user provides cable.')}
                {mismatchHint('connectorFormat1')}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Socket Type</label>
              <select
                value={wizardData.socketType1 ?? '10'}
                onChange={(e) => onChange({ socketType1: e.target.value })}
                className={selectClass}
              >
                {SOCKET_TYPE_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
              {hint('Physical plug standard. Type 2 is the EU standard for AC charging.')}
              {mismatchHint('socketType1')}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phases</label>
                <select
                  value={wizardData.noOfPhases1 ?? '3'}
                  onChange={(e) => onChange({ noOfPhases1: e.target.value })}
                  className={selectClass}
                >
                  {PHASES_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
                {hint('3-phase: up to 22 kW. 1-phase: up to ~7.4 kW at 32 A.')}
                {mismatchHint('noOfPhases1')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charging Type</label>
                <select
                  value={wizardData.chargingType1 ?? '1'}
                  onChange={(e) => onChange({ chargingType1: e.target.value })}
                  className={selectClass}
                >
                  {CHARGING_TYPE_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
                {hint('AC uses on-board charger. DC charges battery directly (faster, higher power).')}
                {mismatchHint('chargingType1')}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Current [A]</label>
                <select
                  value={wizardData.maxCurrentEvse1 ?? '32'}
                  onChange={(e) => onChange({ maxCurrentEvse1: e.target.value })}
                  className={selectClass}
                >
                  {MAX_CURRENT_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v} A</option>
                  ))}
                </select>
                {hint('Max EVSE current for smart charging profiles.')}
                {mismatchHint('maxCurrentEvse1')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Current [A]</label>
                <input
                  type="number"
                  min="1"
                  value={wizardData.minCurrentEvse1 ?? '6'}
                  onChange={(e) => onChange({ minCurrentEvse1: e.target.value })}
                  className={inputClass}
                />
                {hint('Integer. Min EVSE current. Typically 6 A (IEC 61851).')}
                {mismatchHint('minCurrentEvse1')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Power [kW]</label>
                <input
                  type="number"
                  min="1"
                  value={wizardData.maxPowerKw1 ?? '22'}
                  onChange={(e) => onChange({ maxPowerKw1: e.target.value })}
                  className={inputClass}
                />
                {hint('Decimal. e.g. 32 A × 230 V × 3 = 22.1 kW.')}
                {mismatchHint('maxPowerKw1')}
              </div>
            </div>
          </div>
        )}

        {/* ── Connector 2 (Duo only) ──────────────────────────────────────── */}
        {isDuoEv && (
          <div className="rounded-lg border border-gray-200 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Connector 2</h3>
              {mismatches?.length && sectionHasMismatches(CONNECTOR2_FIELDS) && (
                <button
                  type="button"
                  onClick={() => applyDefaults(CONNECTOR2_FIELDS)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Apply Defaults
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
              <select
                value={wizardData.connectorFormat2 ?? '2'}
                onChange={(e) => onChange({ connectorFormat2: e.target.value })}
                className={selectClass}
              >
                {CONNECTOR_FORMAT_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
              {hint('Cable = tethered cable fixed to charger. Socket = open socket, user provides cable.')}
              {mismatchHint('connectorFormat2')}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Socket Type</label>
              <select
                value={wizardData.socketType2 ?? '10'}
                onChange={(e) => onChange({ socketType2: e.target.value })}
                className={selectClass}
              >
                {SOCKET_TYPE_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
              {hint('Physical plug standard. Type 2 is the EU standard for AC charging.')}
              {mismatchHint('socketType2')}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phases</label>
                <select
                  value={wizardData.noOfPhases2 ?? '3'}
                  onChange={(e) => onChange({ noOfPhases2: e.target.value })}
                  className={selectClass}
                >
                  {PHASES_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
                {hint('3-phase: up to 22 kW. 1-phase: up to ~7.4 kW at 32 A.')}
                {mismatchHint('noOfPhases2')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charging Type</label>
                <select
                  value={wizardData.chargingType2 ?? '1'}
                  onChange={(e) => onChange({ chargingType2: e.target.value })}
                  className={selectClass}
                >
                  {CHARGING_TYPE_OPTIONS.map((o) => (
                    <option key={o.code} value={o.code}>{o.label}</option>
                  ))}
                </select>
                {hint('AC uses on-board charger. DC charges battery directly (faster, higher power).')}
                {mismatchHint('chargingType2')}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Current [A]</label>
                <select
                  value={wizardData.maxCurrentEvse2 ?? '32'}
                  onChange={(e) => onChange({ maxCurrentEvse2: e.target.value })}
                  className={selectClass}
                >
                  {MAX_CURRENT_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v} A</option>
                  ))}
                </select>
                {hint('Max EVSE current for smart charging profiles.')}
                {mismatchHint('maxCurrentEvse2')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Current [A]</label>
                <input
                  type="number"
                  min="1"
                  value={wizardData.minCurrentEvse2 ?? '6'}
                  onChange={(e) => onChange({ minCurrentEvse2: e.target.value })}
                  className={inputClass}
                />
                {hint('Integer. Min EVSE current. Typically 6 A (IEC 61851).')}
                {mismatchHint('minCurrentEvse2')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Power [kW]</label>
                <input
                  type="number"
                  min="1"
                  value={wizardData.maxPowerKw2 ?? '22'}
                  onChange={(e) => onChange({ maxPowerKw2: e.target.value })}
                  className={inputClass}
                />
                {hint('Decimal. e.g. 32 A × 230 V × 3 = 22.1 kW.')}
                {mismatchHint('maxPowerKw2')}
              </div>
            </div>
          </div>
        )}

        {/* ── Custom (wizard-visible) attributes ──────────────────────────── */}
        {(() => {
          // Only exclude wizard attrs that are already rendered by the hardcoded sections above.
          // Attrs like 7015 (chargerMaxCurrent) should still appear if their group check fails
          // but the admin marked them as wizard-visible.
          const renderedFields: (keyof WizardData)[] = [
            ...PAYMENT_FIELDS, // always rendered
            ...(isEvCharger ? [...GENERAL_FIELDS, ...EV_CHARGER_FIELDS] : []),
            ...(isEvCharger ? CONNECTOR1_FIELDS : []),
            ...(isDuoEv ? CONNECTOR2_FIELDS : []),
            ...(hasEvse ? VPOST_CHARGER_FIELDS : []),
          ];
          const renderedAttrIds = new Set(renderedFields.map((f) => String(FIELD_ATTR_ID[f])).filter(Boolean));
          const customAttrs = (selectedMachineType?.wizardAttributes ?? []).filter(
            (a) => !renderedAttrIds.has(String(a.attrId))
          );
          if (customAttrs.length === 0) return null;
          return (
          <>
            {sectionHeader('Custom Settings')}
            {customAttrs.map((attr) => {
              const currentVal = (wizardData.customAttributeValues ?? {})[String(attr.attrId)] ?? attr.defaultValue;
              return (
                <div key={attr.attrId}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {attr.attributeName || `Attribute ${attr.attrId}`}
                    <span className="ml-1.5 text-xs text-gray-400 font-normal">#{attr.attrId}</span>
                  </label>
                  <input
                    type="text"
                    value={currentVal}
                    onChange={(e) =>
                      onChange({
                        customAttributeValues: {
                          ...(wizardData.customAttributeValues ?? {}),
                          [String(attr.attrId)]: e.target.value,
                        },
                      })
                    }
                    placeholder={attr.defaultValue || 'Enter value…'}
                    className={inputClass}
                  />
                  {hint('String. Admin-configured attribute — check the attribute catalog for expected format and valid values.')}
                </div>
              );
            })}
          </>
          );
        })()}

      </div>
    </div>
  );
}
